import React, { useState, useEffect, useRef, useMemo } from "react";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import "./CreateConsumableList.css";
import { getLocalizedText } from "../../../../utils/localization";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetGetAllGroups, resetCreateConsumable, resetCreateGroup } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableSlice";
import { getAllGroups, createConsumable, createGroup } from "../../../../modules/NursignCare/AdditonalConsumable/AddConsumableThunk";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import useValidation from "../../../../utils/validators";

const CreateConsumableList = () => {
  const { t, i18n } = useTranslation();
  const { getAllGroupErr, getAllGroupSuccess, createConsumableSuccess, createConsumableErr, createGroupSuccess, createGroupErr } = useSelector((state) => state.addConsumable);
  const { validateCreateConsumable } = useValidation();
  const dispatch = useDispatch();
  const nav = useNavigate();
  const location = useLocation();
  const breadcrumbs = ["nursingCare", "manageResource", "additionalConsumables"];
  const [inputModal, setInputModal] = useState(false);
  const [itemName, setItemName] = useState({ en: "", vi: "" });
  const [selectedLang, setSelectedLang] = useState({
    value: i18n.language.toUpperCase(),
    label: i18n.language === "vi" ? t("Vietnamese") : t("English"),
  });
  const [pendingGroupId, setPendingGroupId] = useState(null);
  const langOptions = [
    { value: "VI", label: t("Vietnamese") },
    { value: "EN", label: t("English") },
  ];

  useEffect(() => {
    setSelectedLang({
      value: i18n.language.toUpperCase(),
      label: i18n.language === "vi" ? t("Vietnamese") : t("English"),
    });
  }, [i18n.language, t]);
  const [code, setCode] = useState("");
  const [numPcs, setNumPcs] = useState("");
  const [opdInternational, setOpdInternational] = useState("");
  const [opdLocal, setOpdLocal] = useState("");
  const [opdSubsidized, setOpdSubsidized] = useState("");
  const [selectedUOM, setSelectedUOM] = useState({ value: "", label: "" });
  const modalRef = useRef(null);
  const paths = [strings.navigation.manageNurseReq, strings.navigation.additionalConsumableList, location.pathname];
  const options = [
    { value: "PCS", label: t("PCS") },
    { value: "PAIR", label: t("Pair") },
    { value: "PACK", label: t("Pack") },
  ];
  const [rawGroups, setRawGroups] = useState([]);
  const [selectedGrp, setSelectedGrp] = useState({});

  const groups = useMemo(() => {
    return rawGroups.map((item) => ({
      value: item._id,
      label: getLocalizedText(item.group, selectedLang.value.toLowerCase()),
    }));
  }, [rawGroups, selectedLang.value]);

  useEffect(() => {
    if (getAllGroupErr) {
      ToastPopup.error(t("failedToLoadGroups"));
    }
    if (getAllGroupSuccess && getAllGroupSuccess.status === "success") {
      setRawGroups(getAllGroupSuccess.data.groups);

      if (pendingGroupId) {
        const matchingGrp = getAllGroupSuccess.data.groups.find((g) => g._id === pendingGroupId);
        if (matchingGrp) {
          setSelectedGrp({
            value: matchingGrp._id,
            label: getLocalizedText(matchingGrp.group, i18n.language),
          });
          setPendingGroupId(null);
        }
      }
    }
    dispatch(resetGetAllGroups());
  }, [getAllGroupSuccess, getAllGroupErr, i18n.language, pendingGroupId]);

  useEffect(() => {
    if (selectedGrp && selectedGrp.value) {
      const currentGrp = rawGroups.find((g) => g._id === selectedGrp.value);
      if (currentGrp) {
        setSelectedGrp({
          value: currentGrp._id,
          label: getLocalizedText(currentGrp.group, selectedLang.value.toLowerCase()),
        });
      }
    }
  }, [selectedLang.value, rawGroups]);

  useEffect(() => {
    if (createConsumableErr) {
      ToastPopup.error(t("failedToCreateConsumable"));
    }
    if (createConsumableSuccess && createConsumableSuccess.status === "success") {
      nav(strings.navigation.additionalConsumableList);
      ToastPopup.success(t("consumableCreated", { name: createConsumableSuccess.data.consumable.code }));
    }
    dispatch(resetCreateConsumable());
  }, [createConsumableSuccess, createConsumableErr]);

  useEffect(() => {
    if (createGroupErr) {
      const errMsg = typeof createGroupErr === "string" ? createGroupErr : createGroupErr?.message || "";
      if (/duplicate|already exist|exists/i.test(errMsg)) {
        ToastPopup.error(t("duplicateCareGroupName"));
      } else {
        ToastPopup.error(t("failedToCreateGroup"));
      }
    }
    if (createGroupSuccess && createGroupSuccess.status === "success") {
      const newGrpName = getLocalizedText(createGroupSuccess.data.newGroup.group, i18n.language);
      ToastPopup.success(t("grpCreated", { name: newGrpName }));
      setPendingGroupId(createGroupSuccess.data.newGroup._id);
      dispatch(getAllGroups());
    }
    dispatch(resetCreateGroup());
  }, [createGroupSuccess, createGroupErr, i18n.language]);

  useEffect(() => {
    dispatch(getAllGroups());
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        console.log("first");
        setInputModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

 const inputChange = (e) => {
  const { name, value } = e.target;

  if (name.startsWith("itemName_")) {
    const lang = name.split("_")[1]; 
    setItemName((prev) => ({
      ...prev,
      [lang]: value.trimStart(),
    }));
    return;
  }

  const setters = {
    numPcs: setNumPcs,
    opdInternational: setOpdInternational,
    opdLocal: setOpdLocal,
    opdSubsidized: setOpdSubsidized,
    code: setCode,
  };

  if (["numPcs", "opdInternational", "opdLocal", "opdSubsidized"].includes(name)) {
    const noLeadingZero = value.replace(/^0+/, "");
    const formattedValue = noLeadingZero.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    setters[name](formattedValue);
  } else if (name === "code") {
    setters[name](value.toUpperCase().trimStart());
  } else if (setters[name]) {
    setters[name](value.trimStart());
  }
};

  const handleSave = () => {
    const body = {
      group: selectedGrp.value,
      code: code,
      itemName: itemName,
      stockUOM: selectedUOM.value,
      numberOfPcs: numPcs.replace(/,/g, ""),
      price: {
        opdInternationalPrice: opdInternational.replace(/,/g, ""),
        opdLocalPrice: opdLocal.replace(/,/g, ""),
        opdSubsidized: opdSubsidized.replace(/,/g, ""),
      },
      country: "6641f4beeb9fcc2db568c1f8",
    };
    if (validateCreateConsumable(body)) {
      dispatch(createConsumable({ body }));
    }
  };
  const handleCreateGroup = (inputs) => {
    setInputModal(false);
    dispatch(
      createGroup({
        body: {
          group: {
            en: (inputs?.en || "").trim(),
            vi: (inputs?.vi || inputs?.en || "").trim(),
          },
        },
      }),
    );
  };

  return (
    <div className="dp-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="dp-heading-flex">
        <div className="department-heading">{t("createAdditionalConsumables")}</div>
        <Widgets.NewSelectDropdown options={langOptions} big placeholder={t("selLang")} onChange={(i) => setSelectedLang(i)} selectedValue={selectedLang} />
      </div>
      <div className="cac-container">
        <div style={{ position: "relative" }}>
          <p className="cac-dropdown-label">
            {t("careGrpName")}
            <span className="asterik">*</span>
          </p>
          <Widgets.NewSelectDropdown options={groups} big placeholder={t("selGrpName")} onChange={(i) => setSelectedGrp(i)} selectedValue={selectedGrp} />
          <div ref={modalRef}>
            <p className="cac-create-underline" onClick={() => setInputModal(true)}>
              {t("createGrp")}
            </p>
            <Widgets.LocalizedFloatingInput showModal={inputModal} handleCancel={() => setInputModal(false)} handleConfirm={handleCreateGroup} title={t("createCareGrp")} placeholder="entGrpName" />
          </div>
        </div>
        <div className="cac-body">
          <div className="cac-input-grid">
            <div className="cac-inputs pdl-hn">
              <label>
                {t("code")}
                <span className="asterik">*</span>
              </label>
              <input type="text" className="pdl-number" onChange={inputChange} placeholder={t("code")} name="code" value={code} />
            </div>
            <div className="cac-inputs pdl-hn">
              <label>
                {t("itemName")} {selectedLang.value === "EN" ? "(En)" : "(Vi)"}
                <span className="asterik">*</span>
              </label>
              <input
  type="text"
  className="pdl-number"
  onChange={inputChange}
  placeholder={selectedLang.value === "EN" ? t("itemName") : t("itemName") + " (VI)"}
  value={selectedLang.value === "EN" ? itemName.en : itemName.vi}
  name={`itemName_${selectedLang.value.toLowerCase()}`}
/>
            </div>
            <div className="cac-inputs pdl-hn">
              <label>
                {t("stockUOM")}
                <span className="asterik">*</span>
              </label>
              <div className="cac-sel-dropdown">
                <Widgets.NewSelectDropdown options={options} big placeholder={t("entStockUom")} noMargin selectedValue={selectedUOM} onChange={(i) => setSelectedUOM(i)} />
              </div>
            </div>
            <div className="cac-inputs pdl-hn">
              <label>
                {t("noOfPieces")}
                <span className="asterik">*</span>
              </label>
              <input type="text" className="pdl-number" onChange={inputChange} placeholder={t("noOfPieces")} value={numPcs} name="numPcs" />
            </div>
          </div>
          <div className="cac-price-box">
            <section className="cac-title-hr">
              <p className="cac-price-title">Price</p>
              <p className="cac-price-hr"></p>
            </section>
            <div className="dp-price-opd">
              <div className="dp-boxes cac-opd-box">
                <p>{t("opdInterInsured")}</p>
                <div>
                  <span>₫</span>
                  <input type="text" placeholder="0" onChange={inputChange} value={opdInternational} name="opdInternational" />
                </div>
              </div>
              <div className="dp-boxes cac-opd-box">
                <p>{t("opdLocalInsured")}</p>
                <div>
                  <span>₫</span>
                  <input type="text" placeholder="0" onChange={inputChange} value={opdLocal} name="opdLocal" />
                </div>
              </div>
              <div className="dp-boxes cac-opd-box">
                <p>{t("opdSubsidized")}</p>
                <div>
                  <span>₫</span>
                  <input type="text" placeholder="0" onChange={inputChange} value={opdSubsidized} name="opdSubsidized" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="save-speciality cac-save">
          <div className="speciality-cancel" onClick={() => nav(strings.navigation.additionalConsumableList)}>
            {t("cancel")}
          </div>
          <div className="speciality-save" onClick={handleSave}>
            {t("createEntry")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateConsumableList;
